import { ProductData, ProductImage, Specification } from '../product-types';
import { For, Show, createSignal, onCleanup, onMount, useContext } from 'solid-js';
import clsx from 'clsx';
import { Heading } from '../../../ui-components/heading/heading';
import { Button } from '../../../ui-components/button/button';
import { Dropdown } from '../../../ui-components/dropdown/dropdown';
import { QuantityStepper } from '../../../ui-components/quantity-stepper/quantity-stepper';
import { CrossIcon } from '../../icons-library/cross';
import { AppContext } from '../../../app-context-provider/app-context-provider';
import { specificationComponentId } from '../product';
import { navigateToAnchor } from '../../../tools/scroll-to-element';
import theme from '../../../style/theme';
import { BasketContext } from '../../basket/basket-context-provider';
import { A } from '@solidjs/router';

type ProductShopToolbarProps = {
    productData: ProductData;
};

const SKU_TRIGGER_ID = 'product-shop-toolbar-sku-trigger';
const SKU_LIST_ID = 'product-shop-toolbar-sku-list';

export const getImageUrl = (images?: ProductImage[]) => {
    if (!images?.length) {
        return;
    }

    const featuredImage = images.find((image) => image.isFeatured);
    const image = featuredImage || images[ 0 ];
    const imageUrl = image.file.url;
    const imageRatio = image.file.width / image.file.height;

    return { imageUrl, imageRatio };
};

export const ProductShopToolbar = (props: ProductShopToolbarProps) => {
    const { shop, userState, localize, viewport } = useContext(AppContext);
    const { setEmail, addNewOrderLine } = useContext(BasketContext);
    const [ selectedSku, setSelectedSku ] = createSignal<Specification>(props.productData.specifications[ 0 ]);
    const [ amount, setAmount ] = createSignal<number>(1);
    const [ toastMessage, setToastMessage ] = createSignal<string>('');
    const [ skuOpen, setSkuOpen ] = createSignal(false);

    let skuContainerRef: HTMLDivElement | undefined;

    const isMobile = () => viewport.width <= theme.breakpoints.MOBILE;

    const changeAmount = (newAmount: number) => {
        if (amount() + newAmount < 1) return;
        setAmount(amount() + newAmount);
    };

    const addToBasket = () => {
        // Toggle off then back on (in the next tick) so the toast element unmounts
        // and remounts — the @starting-style fade replays on every add.
        setToastMessage('');
        setTimeout(() => {
            setToastMessage(`${amount() ? `${amount()} x ` : ''}"${selectedSku()?.name}" ${localize('added-to-basket', 'added to basket')}`);
        }, 0);
        setEmail(userState.user?.email || '');

        const image = getImageUrl(props.productData.images);

        addNewOrderLine({
            id: props.productData.id,
            externalId: props.productData.externalId,
            postTitle: props.productData.post_title,
            slug: props.productData.slug,
            type: props.productData.type,
            skuRefNumber: selectedSku().refNumber,
            amount: amount(),
            skuDescription: selectedSku().description || '',
            skuName: selectedSku().name,
            skuQuantity: parseInt(selectedSku().quantity || '1'),
            imageUrl: image?.imageUrl,
            imageRatio: image?.imageRatio,
        });
    };

    const selectSku = (sku: Specification) => {
        setAmount(1);
        setSelectedSku(sku);
    };

    const handleFocusOut = (e: FocusEvent) => {
        const nextFocused = e.relatedTarget as Node | null;

        if (!nextFocused) {
            return;
        }

        if (skuContainerRef && !skuContainerRef.contains(nextFocused)) {
            setSkuOpen(false);
        }
    };

    onMount(() => {
        const onDocumentMouseDown = (e: MouseEvent) => {
            if (!skuOpen()) {
                return;
            }
            if (skuContainerRef && !skuContainerRef.contains(e.target as Node)) {
                setSkuOpen(false);
            }
        };

        const onDocumentKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && skuOpen()) {
                setSkuOpen(false);
                document.getElementById(SKU_TRIGGER_ID)?.focus();
            }
        };

        document.addEventListener('mousedown', onDocumentMouseDown);
        document.addEventListener('keydown', onDocumentKeyDown);

        onCleanup(() => {
            document.removeEventListener('mousedown', onDocumentMouseDown);
            document.removeEventListener('keydown', onDocumentKeyDown);
        });
    });

    return (
        <div class="flex flex-col gap-cvi-6">
            <Heading tag="h2" variant='heading-lg' noBlockSpacing>
                {localize('purchase', 'Purchase')}
            </Heading>

            <div class="relative" ref={skuContainerRef} onFocusOut={handleFocusOut}>
                <Dropdown
                    id={SKU_TRIGGER_ID}
                    label={selectedSku()?.name}
                    expanded={skuOpen()}
                    onToggle={() => setSkuOpen(!skuOpen())}
                    aria-controls={SKU_LIST_ID}
                    aria-label={localize('select-specification', 'Select a specification from the list')}
                />
                <Show when={skuOpen()}>
                    <ul
                        id={SKU_LIST_ID}
                        role="listbox"
                        aria-labelledby={SKU_TRIGGER_ID}
                        class={clsx(
                            'absolute left-0 right-0 top-full mt-cvi-2 z-10',
                            'bg-cvi-bg-default border border-cvi-border-muted rounded-cvi-md shadow-elevation-overlay',
                            'max-h-[20rem] overflow-y-auto',
                        )}
                    >
                        <For each={props.productData.specifications}>{(sku: Specification) => (
                            <li role="option" class="pl-0">
                                <button
                                    type="button"
                                    class={clsx(
                                        'flex w-full items-baseline justify-between gap-cvi-5',
                                        'px-cvi-5 py-cvi-5',
                                        'border-0 bg-transparent text-left cursor-pointer transition-colors',
                                        'hover:bg-cvi-bg-menu-hover focus-visible:bg-cvi-bg-menu-hover',
                                    )}
                                    onClick={() => {
                                        selectSku(sku);
                                        setSkuOpen(false);
                                    }}
                                >
                                    <span class="label-md font-cvi-medium text-cvi-fg-default">
                                        {sku.name}
                                    </span>
                                    <span class="label-sm text-cvi-fg-muted whitespace-nowrap">
                                        {sku.refNumber}
                                    </span>
                                </button>
                            </li>
                        )}</For>
                    </ul>
                </Show>
            </div>

            <a
                href={`#${specificationComponentId}`}
                class="label-sm font-cvi-medium text-cvi-fg-link no-underline hover:underline pt-cvi-3"
                onClick={(e: MouseEvent) => {
                    navigateToAnchor({
                        event: e,
                        id: specificationComponentId,
                        isCareSite: true,
                        smallScreen: isMobile(),
                    });
                }}
            >
                {localize('view-specifications', 'View specifications')}
            </a>

            <Show when={selectedSku()}>
                <div class="flex flex-col gap-cvi-8 justify-center items-start flex-wrap self-stretch bg-cvi-bg-neutral-subtle p-cvi-6 rounded-cvi-md">
                    <div class="text-cvi-fg-subtle body-sm flex flex-col gap-cvi-4">
                        <p>
                            <span>{localize('ref-nr', 'Reference number')}: </span>
                            <span class="text-cvi-fg-default">{selectedSku()?.refNumber}</span>
                        </p>
                        <p>
                            <span>{localize('units-per-pack', 'Units per pack')}: </span>
                            <span class="text-cvi-fg-default">{`${selectedSku()?.quantity || 1}`}</span>
                        </p>
                        <p class="text-cvi-fg-default">
                            {selectedSku()?.description}
                        </p>
                    </div>
                    <div class="flex flex-row justify-between gap-cvi-6 items-center self-stretch">
                        <QuantityStepper
                            amount={amount()}
                            onDecrease={() => changeAmount(-1)}
                            onIncrease={() => changeAmount(1)}
                            minAmount={1}
                        />
                        <Button
                            onClick={addToBasket}
                            disabled={!selectedSku()}
                            label={localize('add-to-basket', 'Add to basket')}
                        />
                    </div>
                </div>
            </Show>

            <Show when={toastMessage()}>
                <div
                    role="status"
                    aria-live="polite"
                    class="relative flex gap-cvi-5 items-start p-cvi-6 self-stretch rounded-cvi-md bg-cvi-bg-status-highlight opacity-100 transition-opacity duration-500 ease-in-out [@starting-style]:opacity-0"
                >
                    <div class="flex-1 min-w-0 flex flex-col gap-cvi-2 items-start pt-cvi-1">
                        <p class="body-sm font-cvi-bold text-cvi-fg-default">{toastMessage()}</p>
                        <Show when={shop.basketPage.url}>
                            <A
                                href={shop.basketPage.url}
                                class="body-sm font-cvi-regular text-cvi-fg-default no-underline hover:underline whitespace-nowrap"
                            >
                                {localize('view-basket', 'View basket')}
                            </A>
                        </Show>
                    </div>
                    <button
                        type="button"
                        class="icon-button-ghost icon-button-ghost-sm absolute top-cvi-3 right-cvi-3"
                        onClick={() => setToastMessage('')}
                        aria-label={localize('close', 'Close')}
                    >
                        <CrossIcon fill="currentColor" size="small" />
                    </button>
                </div>
            </Show>
        </div>
    );
};

export default ProductShopToolbar;
